import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Hexagon, Printer, ArrowLeft, Phone, Mail, Shield } from "lucide-react";
import { PricingGrid, PRICING_TIERS } from "@/components/pricing/PricingContent";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";

// ---------------------------------------------------------------------------
// Public /pricing — ungated, print-ready page.
// ?print=1 triggers window.print() on load so the "Export PDF" button from
// the pitch deck can open this URL and immediately invoke the browser's
// native print-to-PDF dialog.
// ---------------------------------------------------------------------------

export default function PricingPage() {
  useDocumentTitle("OmniDraftLab · Pricing");
  const [params] = useSearchParams();
  const [printQueued, setPrintQueued] = useState(false);

  useEffect(() => {
    if (params.get("print") === "1" && !printQueued) {
      setPrintQueued(true);
      // Give fonts + images a beat to settle before the dialog opens
      const t = setTimeout(() => window.print(), 650);
      return () => clearTimeout(t);
    }
  }, [params, printQueued]);

  return (
    <div
      className="min-h-screen text-1 print:min-h-0"
      style={{
        background:
          "radial-gradient(ellipse 80% 60% at 20% 10%, rgba(34,211,238,0.08), transparent 60%), radial-gradient(ellipse 70% 50% at 85% 85%, rgba(251,113,133,0.06), transparent 60%), #04111A",
      }}
      data-testid="pricing-page"
    >
      {/* Top bar — hidden on print */}
      <div className="no-print sticky top-0 z-20 backdrop-blur-md bg-[#04111A]/70 border-b border-white/5">
        <div className="max-w-[1500px] mx-auto px-6 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-1 hover:text-cyan-300 transition text-sm">
            <Hexagon className="h-4 w-4 text-cyan-300" strokeWidth={1.6} />
            <span className="font-semibold tracking-tight">OmniDraftLab</span>
          </Link>
          <div className="flex items-center gap-2">
            <button
              data-testid="pricing-page-print"
              onClick={() => window.print()}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs bg-cyan-300 text-[#04111A] font-semibold hover:bg-cyan-200 transition"
            >
              <Printer className="h-3.5 w-3.5" /> Save as PDF
            </button>
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs bg-white/[0.04] ring-1 ring-white/10 hover:ring-cyan-300/30 text-2 hover:text-1 transition"
            >
              <ArrowLeft className="h-3.5 w-3.5" /> Back to site
            </Link>
          </div>
        </div>
      </div>

      {/* Main — vertically centered on screen, flows naturally on print */}
      <div className="max-w-[1500px] mx-auto px-6 py-10 sm:py-14 print:py-4 min-h-[calc(100vh-64px)] print:min-h-0 flex flex-col justify-center">
        {/* Eyebrow + headline */}
        <div className="text-center max-w-3xl mx-auto">
          <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-cyan-300 mb-4">
            <span className="h-px w-10 bg-cyan-300/60" /> Engagement Tiers
          </div>
          <h1
            className="font-semibold tracking-tight leading-[1.05] text-1"
            style={{ fontSize: "clamp(2rem, 4.4vw, 3.6rem)" }}
          >
            Built for <span className="text-cyan-300 italic font-light">serious</span> prospects only.
          </h1>
          <p className="mt-5 text-2 text-base sm:text-lg leading-relaxed max-w-2xl mx-auto">
            Four engagement depths. Every tier is application-only — a limited number of clients per draft class so every athlete gets the full attention of the advisory team.
          </p>
        </div>

        {/* The 4 cards */}
        <div className="mt-12 print:mt-6">
          <PricingGrid />
        </div>

        {/* Footer block — trust + contact */}
        <div className="mt-12 print:mt-8 grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5 max-w-5xl mx-auto w-full">
          <FooterCard
            icon={Shield}
            title="What every tier includes"
            body="Direct access to a former NFL personnel executive. Written reports, protected deliverables, and a one-pager summary after every major deliverable."
          />
          <FooterCard
            icon={Phone}
            title="How to apply"
            body="15-minute intro call with the founder. Bring your timeline, your athlete's tape, and your questions. We'll return a tier recommendation and a written proposal within 72 hours."
          />
          <FooterCard
            icon={Mail}
            title="Contact"
            body={<>
              <a className="text-cyan-300 hover:text-cyan-200" href="mailto:hello@omnidraftlab.com">hello@omnidraftlab.com</a>
              <div className="mt-1 text-3 text-xs">Applications reviewed weekly · replies within 48 hrs.</div>
            </>}
          />
        </div>

        {/* Fine print */}
        <div className="mt-10 print:mt-6 text-center text-3 text-[11px] leading-relaxed max-w-3xl mx-auto italic">
          All engagements require an executed advisory agreement. Pricing ranges reflect complexity of the prospect, timeline, and scope of deliverables. Outcomes belong to teams; readiness — across all four layers — belongs to us.
        </div>

        {/* Footer bar with timestamp */}
        <div className="mt-10 print:mt-6 text-center text-3 text-[10px] uppercase tracking-[0.22em]">
          OmniDraftLab · Draft Class {new Date().getFullYear() + 1} · omnidraftlab.com
        </div>
      </div>

      {/* Print styles — landscape Letter, dark theme PRESERVED */}
      <style>{`
        @page { size: letter landscape; margin: 0.35in; }

        @media print {
          html, body {
            background: #04111A !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          [data-testid="pricing-page"] {
            background: #04111A !important;
            background-image:
              radial-gradient(ellipse 80% 60% at 20% 10%, rgba(34,211,238,0.08), transparent 60%),
              radial-gradient(ellipse 70% 50% at 85% 85%, rgba(251,113,133,0.06), transparent 60%) !important;
            min-height: 0 !important;
            color: #E6F0F5 !important;
          }

          /* Hide chrome */
          .no-print { display: none !important; }
          .print-only { display: none !important; }

          /* Tighten layout to a single landscape sheet */
          [data-testid="pricing-page"] > div.max-w-\\[1500px\\] {
            padding: 0 !important;
            min-height: 0 !important;
            max-width: 100% !important;
            display: block !important;
          }
          [data-testid="pricing-page"] h1 { font-size: 2.1rem !important; line-height: 1.05 !important; }
          [data-testid="pricing-page"] .text-center.max-w-3xl > p { font-size: 0.85rem !important; margin-top: 0.6rem !important; }
          [data-testid="pricing-page"] .grid.grid-cols-1.md\\:grid-cols-3 { margin-top: 1rem !important; }

          /* Force the 4-card row to stay 4-up on landscape, regardless of viewport */
          [data-testid="pricing-page"] .grid.grid-cols-1.md\\:grid-cols-2.lg\\:grid-cols-4 {
            grid-template-columns: repeat(4, minmax(0, 1fr)) !important;
            gap: 0.6rem !important;
          }

          /* Compact tier cards */
          [data-testid^="pricing-card-"] {
            padding: 0.9rem 1rem !important;
            border-radius: 12px !important;
          }
          [data-testid^="pricing-card-"] h3 { font-size: 0.95rem !important; line-height: 1.25 !important; margin-top: 0.6rem !important; }
          [data-testid^="pricing-card-"] p  { font-size: 0.72rem !important; line-height: 1.35 !important; margin-top: 0.4rem !important; }
          [data-testid^="pricing-card-"] li { font-size: 0.72rem !important; line-height: 1.3 !important; }
          [data-testid^="pricing-card-"] ul > li + li { margin-top: 0.25rem !important; }

          /* Hide "Apply with this tier" CTA on print — leave-behind is read, not clicked */
          [data-testid^="pricing-apply-"] { display: none !important; }

          /* Ensure single page */
          [data-testid="pricing-page"], [data-testid="pricing-page"] > * { page-break-inside: avoid; break-inside: avoid; }
        }
      `}</style>
    </div>
  );
}

function FooterCard({ icon: Icon, title, body }) {
  return (
    <div className="rounded-xl bg-white/[0.03] ring-1 ring-white/10 p-5 text-left">
      <div className="flex items-center gap-2 text-cyan-300">
        <Icon className="h-3.5 w-3.5" />
        <div className="text-[10px] uppercase tracking-[0.22em] font-medium">{title}</div>
      </div>
      <div className="mt-2 text-2 text-sm leading-relaxed">{body}</div>
    </div>
  );
}

// keep the import usable from other places if needed later
export { PRICING_TIERS };
